import { useEffect, useRef } from "react";
import Navbar from "./NavPhc";
import { CheckCircle, ArrowRight } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket, faUsers, faBrain } from "@fortawesome/free-solid-svg-icons";
import about from "../../assets/about.png";
const AboutPage = () => {
  const keyPoints = [
    "Industry-leading expertise in emerging technologies",
    "Customized solutions tailored to your specific business needs",
    "Comprehensive training and support throughout implementation",
    "Continuous research and development to stay ahead of market trends",
    "Strategic partnerships with global technology leaders",
  ];

  const aboutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate");
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = aboutRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <main className="bg-white text-gray-800">
      <Navbar />
      
      {/* --- Hero Section --- */}
      <section className="text-center py-16 md:py-20 px-4 bg-[#0d1c5d]">
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white">
          About <span className="text-white">MNR Technologies</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-white max-w-3xl mx-auto leading-8">
          We believe innovation isn't just about technology — it's about solving the right problems, anticipating change, and empowering people. Our team of experts brings a blend of industry knowledge, technical excellence, and a relentless commitment to quality.
        </p>
      </section>

      {/* --- "Who We Are" Section --- */}
      <section 
        ref={aboutRef} 
        className="py-16 md:py-20 opacity-0 transform translate-y-10 transition-all duration-1000 ease-out [&.animate]:opacity-100 [&.animate]:translate-y-0"
      >
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-10 items-center">
          
          {/* Text Content Column */}
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Who We Are
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-700">
              MNR Technologies is a leading provider of innovative technology solutions, dedicated to helping businesses navigate the complexities of digital transformation. With expertise spanning healthcare, banking, AI, and cloud computing, we deliver tailored solutions that drive growth.
            </p>
            <div className="mt-8 space-y-4">
              {keyPoints.map((point, index) => (
                <div className="flex items-start gap-x-4" key={index}>
                  <CheckCircle
                    className="h-7 w-7 flex-none text-blue-600 mt-1"
                    aria-hidden="true"
                  />
                  <span className="text-lg text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Image Column */}
          <div className="flex justify-center">
            <img
              src={about}
              alt="A modern office with people working on computers"
              className="rounded-2xl shadow-2xl shadow-blue-500/20 w-full max-w-lg"
              onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400'; }}
            />
          </div>

        </div>
      </section>
      
      {/* --- Culture Section --- */}
      <div className="bg-slate-50 py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-7xl flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          
          <section className="lg:w-1/2 flex-1 flex justify-center items-center">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
              alt="Three people working on laptops"
              className="rounded-2xl shadow-xl h-full w-full object-cover max-h-[600px]"
            />
          </section>

          <section className="lg:w-1/2 max-w-xl space-y-5">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Our Culture</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We are a diverse team with a deep commitment to innovation and collaboration. We believe in a culture of continuous learning, growth mindset, and open communication.
            </p>
            <div className="space-y-5 pt-3">
              <article className="bg-white rounded-xl p-6 flex items-start gap-6 shadow-md border border-gray-200 hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faRocket} className="text-blue-600 text-3xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Innovation First</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We foster a culture of continuous innovation, encouraging our team to push boundaries.
                  </p>
                </div>
              </article>

              <article className="bg-white rounded-xl p-6 flex items-start gap-6 shadow-md border border-gray-200 hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-3xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Collaboration</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Our success is built on strong teamwork and open communication across all levels.
                  </p>
                </div>
              </article>

              <article className="bg-white rounded-xl p-6 flex items-start gap-6 shadow-md border border-gray-200 hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300">
                <div className="bg-blue-100 p-4 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faBrain} className="text-blue-600 text-3xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-1">Growth Mindset</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We invest in our team's professional development and encourage learning at every step.
                  </p>
                </div>
              </article>
            </div>
          </section>

        </div>
      </div>
      
      {/* --- Call to Action Section --- */}
      <div className="text-center py-16 md:py-20 px-4 bg-white">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Ready to Transform Your Business?</h2>
        <p className="mt-4 max-w-2xl mx-auto text-gray-600 text-lg leading-8">
          Contact us today to discuss how our solutions and services can help you achieve your business goals.
        </p>
        <a href="/book" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg shadow-blue-500/30 transition-transform duration-300 hover:scale-105">
          Get in Touch
          <ArrowRight className="h-5 w-5" />
        </a>
      </div>
    </main>
  );
};

export default AboutPage;